import { c as _c } from "react/compiler-runtime";
import { useSceneSeatPermission } from "@components/table/members/assign-seats/use-scene-seat-permission";
import { useSeatsAvailable } from "@components/table/members/assign-seats/use-seats-available";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useToast } from "@flat-ui";
import { nounPluralize } from "@utils/data-display";
import { MEMBER_SEAT_LABELS } from "@utils/member-seats-utils";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

/** Where the seats are given from, as tracked */
const LOCATION = "workspace-invite";
/** Returns the seats as the user reads them, e.g. "a Sphere XG and a SCENE seat" */
function getSeatsText(seats) {
  return `${seats.map(seat => `a ${MEMBER_SEAT_LABELS[seat]}`).join(" and ")} seat`;
}

/**
 * Returns how to give the members invited to the workspace their seats, with the requests, the
 * refresh of the seat usage, the tracking and the toasts about it.
 *
 * Each member gets all their seats in one request, since the backend keeps only one of several
 * requests that change the permissions of a member at the same time.
 */
export function useGiveInviteSeats() {
  const $ = _c(7);
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    openToast
  } = useToast();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    setSeatPermissions,
    refreshSeatUsage
  } = useSceneSeatPermission();
  const seatsAvailable = useSeatsAvailable();
  let t0;
  if ($[0] !== handleErrorWithToast || $[1] !== openToast || $[2] !== refreshSeatUsage || $[3] !== seatsAvailable || $[4] !== setSeatPermissions || $[5] !== trackEvent) {
    const giveInviteSeats = async function giveInviteSeats(members, seats) {
      if (!members.length || !seats.length) {
        return;
      }
      const shortSeat = seats.find(seat => seatsAvailable[seat] < members.length);
      if (shortSeat) {
        openToast({
          title: "Cannot assign the seats",
          message: `Not enough ${MEMBER_SEAT_LABELS[shortSeat]} seats are available for ${nounPluralize({
            counter: members.length,
            word: "member"
          })}.`,
          variant: "error"
        });
        return;
      }
      const changes = seats.map(_temp);
      const results = await Promise.allSettled(members.map(member => setSeatPermissions(member, changes)));
      refreshSeatUsage();
      const seatedCount = results.filter(_temp2).length;
      trackSeats(seats, seatedCount);
      results.forEach((result, index) => {
        if (result.status === "rejected") {
          handleErrorWithToast({
            id: `inviteSeats-${members[index].identity}-${Date.now().toString()}`,
            title: "Cannot assign the seats",
            error: result.reason
          });
        }
      });
      if (seatedCount) {
        openToast({
          title: "Seats assigned",
          message: `${nounPluralize({
            counter: seatedCount,
            word: "member"
          })} now ${seatedCount === 1 ? "holds" : "hold"} ${getSeatsText(seats)}.`
        });
      }
    };
    function trackSeats(seats_0, numberOfMembers) {
      if (!numberOfMembers) {
        return;
      }
      if (seats_0.includes("xgSeat")) {
        trackEvent({
          name: WorkspaceEvents.assignXgSeat,
          props: {
            location: LOCATION,
            numberOfMembers
          }
        });
      }
      if (seats_0.includes("sceneSeat")) {
        trackEvent({
          name: WorkspaceEvents.assignSceneSeats,
          props: {
            location: LOCATION,
            numberOfMembers
          }
        });
      }
    }
    t0 = {
      giveInviteSeats
    };
    $[0] = handleErrorWithToast;
    $[1] = openToast;
    $[2] = refreshSeatUsage;
    $[3] = seatsAvailable;
    $[4] = setSeatPermissions;
    $[5] = trackEvent;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  return t0;
}
function _temp2(t0) {
  const {
    status
  } = t0;
  return status === "fulfilled";
}
function _temp(seat_0) {
  return {
    seat: seat_0,
    isAssigning: true
  };
}